import './Queue.css';
import React, { useEffect } from 'react';
import { Container } from '../../components/Container/Container';
import { useMeta } from '../../model/meta';
import { Card } from '../../components/Card/Card';
import { useData, useRemoteData, usePolling, useAsync } from '../../model/data';
import { LoadingPage } from '../../components/LoadingPage/LoadingPage';
import {
  pollQueueData,
  setQueueLimit,
  showActiveSessionsAge,
  showSessionsAge,
  showActiveSessionsWaitingTime,
  QueueStat,
  loadOrdersPerMinute,
  loadTopIps,
  loadTopPaymentFingerprints,
  loadOrdersCreatedPerMinute,
} from '../../model/queue';
import { getAllShops } from '../../model/shop';
import { NumberField } from '../../components/Form/NumberField/NumberField';
import { t } from '../../locales/translate';
import { formatNumber, useErrorHandler } from '../../util/util';
import { Icon } from '../../components/Icon/Icon';
import { BarChart } from '../../components/Charts/BarChart';
import { Label } from '../../components/Form/Label/Label';
import classnames from 'classnames';
import { Grid } from '../../components/Grid/Grid';
import { camelCase } from 'lodash';
import { Spinner } from '../../components/Spinner/Spinner';
import { Scale } from './Scale';
import { Stack } from '../../components/Stack/Stack';
import { displayTimeSeries } from '../../model/statistics';
import { ContributionListChart } from '../../components/Charts/contribution_list_chart';

type KeyValue = { name: string; value: number };

export function Queue(): JSX.Element {
  useMeta({ title: t.page.queue.title });
  const queue = useData((db) => db.get('queue'));
  const [shops, shopsLoading, shopsError] = useRemoteData(getAllShops, (db) =>
    db.table('shops').where({ waitingQueue: true })
  );
  const [loading, error] = useAsync(async () => {
    showActiveSessionsAge();
    showSessionsAge();
    showActiveSessionsWaitingTime();
    loadOrdersPerMinute();
    loadTopIps();
    loadOrdersCreatedPerMinute();
    loadTopPaymentFingerprints();
  }, []);

  useErrorHandler(error);

  const [pollingState, togglePollingState] = usePolling(() => {
    showActiveSessionsAge();
    showSessionsAge();
    showActiveSessionsWaitingTime();
    loadOrdersPerMinute();
    loadTopIps();
    loadOrdersCreatedPerMinute();
    loadTopPaymentFingerprints();
  }, 30000);

  const activeSessionsAge = useData((db) => db.get('queueActiveSessionsAge'));
  const sessionsAge = useData((db) => db.get('queueSessionsAge'));
  const sessionsWaitTime = useData((db) => db.get('queueSessionWaitTime'));
  const [ordersPerMinute, paymentsPerMinute, topIps, ordersCreatedPerMinute, topPaymentFingerprints] = useData((db) => [
    db.get('ordersPerMinute') ?? [],
    db.get('paymentsPerMinute') ?? [],
    db.get('topIps') ?? [],
    db.get('ordersCreatedPerMinute') ?? [],
    db.get('topPaymentFingerprints') ?? [],
  ]);

  const ordersPerMinuteData = displayTimeSeries(
    [
      { name: t.page.queue.ordersPerMinute, series: ordersPerMinute },
      { name: t.page.queue.paymentsPerMinute, series: paymentsPerMinute },
    ],
    { format: 'time' }
  );

  const topIpsData = topIps.map((ip) => {
    return {
      label: ip.ip,
      value: formatNumber(ip.count, 0),
      percentage: ip.count / topIps[0]?.count || 0,
    };
  });

  const ordersCreatedPerMinuteData = displayTimeSeries(
    [{ name: t.page.queue.ordersCreatedPerMinute, series: ordersCreatedPerMinute }],
    { format: 'time' }
  );

  const topPaymentFingerprintsData = topPaymentFingerprints.map((paymentFingerprint) => {
    return {
      label: `${paymentFingerprint.paymentMethodId} - ${paymentFingerprint.fingerprint}`,
      value: formatNumber(paymentFingerprint.count, 0),
      percentage: paymentFingerprint.count / topPaymentFingerprints[0]?.count || 0,
    };
  });

  useErrorHandler(shopsError);

  useEffect(() => {
    const handle = pollQueueData();
    return handle.stop;
  }, []);
  if (!queue) {
    return <LoadingPage />;
  }
  const total = queue.totalActiveUsersCount;
  const max = queue.maximumSpotsCount;
  const waiting = Math.max(0, total - max);
  const active = total - waiting;
  const free = Math.max(0, max - total);

  const catalogMax = queue.maximumCatalogSpotsCount;
  const catalogWaiting = Math.max(0, total - catalogMax);
  const catalogActive = total - catalogWaiting;
  const catalogFree = Math.max(0, catalogMax - total);

  function parseSessionStatData(data?: QueueStat): KeyValue[] {
    const sessionStatArray = new Array(16).fill(0);
    return sessionStatArray.map((_, index) => ({
      name: `${index} min`,
      value: data?.[index] || 0,
    }));
  }

  // Couldn't find a decent return type to capture the
  // Flexability of the function
  function mergeStatData(
    data1: { label: string; data: KeyValue[] },
    data2: { label: string; data: KeyValue[] }
  ): Array<{ [key: string]: number | string }> {
    return data1.data.map((data, index) => {
      return {
        name: data.name,
        [camelCase(data1.label)]: data.value,
        [camelCase(data2.label)]: data2.data[index].value,
      };
    });
  }

  return (
    <Container className="queue">
      <Stack size="lg">
        <Card
          title={t.page.queue.title}
          actions={
            <>
              <NumberField
                label={t.page.queue.maximumSpotsCount}
                value={max}
                required
                onBlur={(maximumSpotsCount) => setQueueLimit({ maximumSpotsCount })}
                decimalDigits={0}
              />
              <NumberField
                label={t.page.queue.maximumCatalogSpotsCount}
                value={queue.maximumCatalogSpotsCount}
                required
                onBlur={(maximumCatalogSpotsCount) => setQueueLimit({ maximumCatalogSpotsCount })}
                decimalDigits={0}
              />
            </>
          }
        >
          <div>{t.page.queue.checkoutQueue}</div>
          <div className="queue-graph">
            {active > 0 && (
              <div className="queue-graph__bar queue-graph__active" style={{ flex: active }}>
                {active} {t.page.queue.active}
              </div>
            )}
            {free > 0 && (
              <div className="queue-graph__bar queue-graph__bar--free" style={{ flex: free }}>
                {free} {t.page.queue.free}
              </div>
            )}
            {waiting > 0 && (
              <div className="queue-graph__bar queue-graph__bar--waiting" style={{ flex: waiting }}>
                {waiting} {t.page.queue.waiting}
              </div>
            )}
          </div>
          <div>{t.page.queue.catalogQueue}</div>
          <div className="queue-graph">
            {active > 0 && (
              <div className="queue-graph__bar queue-graph__bar--active" style={{ flex: catalogActive }}>
                {catalogActive} {t.page.queue.active}
              </div>
            )}
            {free > 0 && (
              <div className="queue-graph__bar queue-graph__bar--free" style={{ flex: catalogFree }}>
                {catalogFree} {t.page.queue.free}
              </div>
            )}
            {waiting > 0 && (
              <div className="queue-graph__bar queue-graph__bar--waiting" style={{ flex: catalogWaiting }}>
                {catalogWaiting} {t.page.queue.waiting}
              </div>
            )}
          </div>
          {!shopsLoading && (
            <p className="queue__shops">{t.page.queue.activeFor(shops.map((e) => e.name).join(', '))}</p>
          )}
        </Card>
        <Card
          title={t.page.queue.waitingQueueStats}
          className={classnames('queue-stats', {
            'live--active': pollingState,
          })}
          actions={<Icon name="refresh" className="live-stats" onClick={() => togglePollingState()} />}
        >
          {loading ? (
            <Spinner color="var(--color-tint)" />
          ) : (
            <Grid>
              <div>
                <Label title={t.page.queue.sessionInactivity} />
                <BarChart
                  data={mergeStatData(
                    {
                      label: t.page.queue.activeSessions,
                      data: parseSessionStatData(activeSessionsAge),
                    },
                    {
                      label: t.page.queue.sessions,
                      data: parseSessionStatData(sessionsAge),
                    }
                  )}
                />
              </div>
              <div>
                <Label title={t.page.queue.waitingTime} />
                <BarChart data={parseSessionStatData(sessionsWaitTime)} />
              </div>
            </Grid>
          )}
        </Card>
        <Card title={t.page.queue.ordersPerMinute} actions={loading && <Icon name="lock" loading />}>
          {ordersPerMinute.length > 0 && <BarChart data={ordersPerMinuteData} />}
        </Card>
        <Card title={t.page.queue.topIps} actions={loading && <Icon name="lock" loading />}>
          <ContributionListChart data={topIpsData} hidePercentage={true} />
        </Card>
        <Card title={t.page.queue.ordersCreatedPerMinute} actions={loading && <Icon name="lock" loading />}>
          {ordersCreatedPerMinute.length > 0 && <BarChart data={ordersCreatedPerMinuteData} />}
        </Card>
        <Card title={t.page.queue.topPaymentFingerprints} actions={loading && <Icon name="lock" loading />}>
          <ContributionListChart data={topPaymentFingerprintsData} hidePercentage={true} />
        </Card>
        <Scale />
      </Stack>
    </Container>
  );
}
